import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { Alert, Card, Typography } from 'antd'
import { format } from 'date-fns'
import { useMemo } from 'react'

import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { ensureArray } from '@/types/api'

import type { PriceResourceType } from './priceUtils'
import { formatPriceTriple, PRICE_ACTIVE_URLS } from './priceUtils'

type AdminPriceInfoDto = components['schemas']['AdminPriceInfoDto']

interface LookupParams {
  resourceType: PriceResourceType
  groupName: string
  clusterId?: string
  vmConfigurationId?: string
  storageType?: string
  replicated?: boolean
  addressType?: string
}

function isReady(params: LookupParams): boolean {
  const { resourceType, groupName, clusterId } = params
  if (!groupName || !clusterId) return false
  if (resourceType === 'vm') return Boolean(params.vmConfigurationId)
  if (resourceType === 'storage') return Boolean(params.storageType)
  return Boolean(params.addressType)
}

function findMatch(
  data: unknown[],
  params: LookupParams,
): AdminPriceInfoDto | null {
  const { resourceType } = params
  for (const item of data) {
    const rec = item as Record<string, unknown>
    const pi = rec.priceInfo as AdminPriceInfoDto | undefined
    if (!pi) continue

    if (resourceType === 'vm') {
      const tid = rec.vmTypeId as { vmConfigurationId: string } | undefined
      if (tid?.vmConfigurationId === params.vmConfigurationId) return pi
    } else if (resourceType === 'storage') {
      const tid = rec.storageTypeId as
        | {
            storageType: string
            replicated: boolean
          }
        | undefined
      if (
        tid?.storageType === params.storageType &&
        tid?.replicated === (params.replicated ?? false)
      )
        return pi
    } else {
      const tid = rec.publicIpTypeId as { addressType: string } | undefined
      if (tid?.addressType === params.addressType) return pi
    }
  }
  return null
}

export function useCurrentPrice(
  params: LookupParams,
): AdminPriceInfoDto | null {
  const ready = isReady(params)

  const query = useCustom<unknown[]>({
    url: `${VODOPAD_ENDPOINT}${PRICE_ACTIVE_URLS[params.resourceType]}`,
    method: 'get',
    config: {
      query: {
        userGroup: params.groupName,
        clusterId: params.clusterId,
      },
    },
    queryOptions: {
      enabled: ready,
      staleTime: 30_000,
    },
  })

  return useMemo(
    () =>
      ready
        ? findMatch(ensureArray<unknown>(query.query.data?.data), params)
        : null,
    [ready, query.query.data?.data, params],
  )
}

function formatDate(d: string) {
  return format(new Date(d), 'MMM d, yyyy')
}

export function CurrentPriceInfo({
  price,
}: {
  price: AdminPriceInfoDto | null
}) {
  if (!price) return null

  const { perSecond, perHour, perMonth } = formatPriceTriple(
    price.pricePerHourPerQty,
  )
  const isIndefinite = !price.activeUntil
  const dateRange = price.activeUntil
    ? `${formatDate(price.activeSince)} – ${formatDate(price.activeUntil)}`
    : `Since ${formatDate(price.activeSince)}`

  return (
    <>
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: 'rgba(255,255,255,0.04)',
        }}
      >
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: 4 }}
        >
          Current active price · {dateRange}
        </Typography.Text>
        <Typography.Text>
          ${perSecond}/s · ${perHour}/h · ${perMonth}/mo
        </Typography.Text>
      </Card>
      {isIndefinite && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The current active price will be automatically expired at the new price's start date."
        />
      )}
    </>
  )
}
